import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Divider,
  InputAdornment,
  ThemeProvider,
  createTheme
} from "@mui/material";

// --- ICONS ---
import BadgeIcon from "@mui/icons-material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import WorkIcon from "@mui/icons-material/Work";
import PublicIcon from "@mui/icons-material/Public";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import WcIcon from "@mui/icons-material/Wc";

import {
  abidjanCommunesList,
  INDENTIFIANT_RECUP_GENRE,
  INDENTIFIANT_RECUP_PAYS,
  INDENTIFIANT_RECUP_PROFESSION,
  INDENTIFIANT_RECUP_VILLE
} from "../services/utils";
import PickListService from "../services/PickListService";

// --- THEME ---
const customTheme = createTheme({
  palette: {
    primary: { main: "#EF6C00", contrastText: "#fff" },
    secondary: { main: "#2E7D32" },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff',
            '&.Mui-focused fieldset': { borderColor: '#EF6C00', borderWidth: 2 },
          },
          '& .MuiInputLabel-root.Mui-focused': { color: '#EF6C00' },
        },
      },
    },
  }
});

// --- LOGIQUE MÉTIER ---
const generateReferenceId = () => {
  const now = new Date();
  const uid = crypto.randomUUID().split("-")[0].toUpperCase();
  return `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}-AGRAC-${uid}`;
};

const calculateAge = (birthDate) => {
  if (!birthDate) return "";
  const today = new Date();
  const dob = new Date(birthDate);
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age >= 0 ? age : "";
};

// --- FORMATAGE TÉLÉPHONE (XX XX XX XX XX) ---
const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, ""); // Garder que les chiffres
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 3) return phoneNumber;
  if (phoneNumberLength < 5) return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2)}`;
  if (phoneNumberLength < 7) return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 4)} ${phoneNumber.slice(4)}`;
  if (phoneNumberLength < 9) return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 4)} ${phoneNumber.slice(4, 6)} ${phoneNumber.slice(6)}`;
  return `${phoneNumber.slice(0, 2)} ${phoneNumber.slice(2, 4)} ${phoneNumber.slice(4, 6)} ${phoneNumber.slice(6, 8)} ${phoneNumber.slice(8, 10)}`;
};

const DialogParticipantContent = ({ handleChange, data, errors }) => {
  const pickListServ = new PickListService();
  const [options, setOptions] = useState({ professions: [], genres: [], pays: [], villes: [] });

  useEffect(() => {
    const fetchOptions = async () => {
      const [professions, genres, pays, villes] = await Promise.all([
        pickListServ.getPickListValues(INDENTIFIANT_RECUP_PROFESSION),
        pickListServ.getPickListValues(INDENTIFIANT_RECUP_GENRE),
        pickListServ.getPickListValues(INDENTIFIANT_RECUP_PAYS),
        pickListServ.getPickListValues(INDENTIFIANT_RECUP_VILLE)
      ]);
      setOptions({ professions, genres, pays, villes });
    };
    fetchOptions();
  }, []);

  useEffect(() => {
    if (!data.referenceId) handleChange({ target: { name: "referenceId", value: generateReferenceId() } });
  }, [data.referenceId]);

  useEffect(() => {
    if (data.dateNaissance) {
      const age = calculateAge(data.dateNaissance);
      if (age !== data.age) handleChange({ target: { name: "age", value: age } });
    }
  }, [data.dateNaissance]);

  // Gestion spécifique pour le téléphone
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    // Limiter à 14 caractères (10 chiffres + 4 espaces)
    if (formatted.length <= 14) {
      handleChange({ target: { name: "telephone", value: formatted } });
    }
  };

  const SectionHeader = ({ title, icon }) => (
    <Box display="flex" alignItems="center" gap={1} mb={2} mt={1}>
      <Box sx={{ color: "primary.main" }}>{icon}</Box>
      <Typography variant="h6" color="primary.dark" fontWeight="bold">{title}</Typography>
      <Divider sx={{ flexGrow: 1, ml: 2, borderColor: 'primary.light' }} />
    </Box>
  );

  return (
    <ThemeProvider theme={customTheme}>
      <Box p={{ xs: 1, md: 3 }}>

        {/* --- 1. IDENTITÉ --- */}
        <SectionHeader title="Informations Personnelles" icon={<BadgeIcon />} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Référence ID" value={data.referenceId || ""} disabled
              InputProps={{ style: { backgroundColor: '#f5f5f5', fontWeight: 'bold' } }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Nom" name="nom" required
              value={data.nom || ""} onChange={handleChange}
              error={!!errors.nom} helperText={errors.nom ? "Nom obligatoire" : ""}
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Prénoms" name="prenoms" required
              value={data.prenoms || ""} onChange={handleChange}
              error={!!errors.prenoms} helperText={errors.prenoms ? "Prénoms obligatoires" : ""}
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon /></InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth label="Date de naissance" type="date" name="dateNaissance" required
              value={data.dateNaissance || ""} onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              error={!!errors.dateNaissance} helperText={errors.dateNaissance ? "Date obligatoire" : ""}
              InputProps={{ startAdornment: <InputAdornment position="start"><CalendarTodayIcon /></InputAdornment> }}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField fullWidth label="Âge" value={data.age || ""} disabled InputProps={{ style: { backgroundColor: '#f5f5f5' } }} />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              select fullWidth label="Genre" name="genre" required
              value={data.genre || ""} onChange={handleChange}
              error={!!errors.genre} helperText={errors.genre ? "Genre requis" : ""}
              InputProps={{ startAdornment: <InputAdornment position="start"><WcIcon /></InputAdornment> }}
            >
              {options.genres.map((opt) => (<MenuItem key={opt.key} value={opt.key}>{opt.name}</MenuItem>))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select fullWidth label="Profession" name="profession"
              value={data.profession || ""} onChange={handleChange}
              InputProps={{ startAdornment: <InputAdornment position="start"><WorkIcon /></InputAdornment> }}
            >
              {options.professions.map((opt) => (<MenuItem key={opt.key} value={opt.key}>{opt.name}</MenuItem>))}
            </TextField>
          </Grid>
          {data.profession === "autre" && (
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Précisez la profession" name="autreProfession" value={data.autreProfession || ""} onChange={handleChange} />
            </Grid>
          )}
        </Grid>

        {/* --- 2. CONTACT (Déplacé ici pour regrouper les obligatoires) --- */}
        <Box mt={4}>
          <SectionHeader title="Contact" icon={<PhoneIcon />} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Téléphone" name="telephone" required
                value={data.telephone || ""}
                onChange={handlePhoneChange} // Utilisation du formateur
                placeholder="XX XX XX XX XX"
                error={!!errors.telephone} helperText={errors.telephone ? "Téléphone requis (10 chiffres)" : ""}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={data.email || ""}
                onChange={handleChange}
                // Ajout de la gestion d'erreur visuelle
                error={!!errors.email}
                helperText={errors.email ? "Format email invalide" : ""}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment>,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* --- 3. LOCALISATION --- */}
        <Box mt={4}>
          <SectionHeader title="Localisation" icon={<PublicIcon />} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Pays" name="pays"
                value={data.pays || ""} onChange={handleChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><PublicIcon /></InputAdornment> }}
              >
                {options.pays.map((opt) => (<MenuItem key={opt.key} value={opt.key}>{opt.name}</MenuItem>))}
              </TextField>
            </Grid>
            {data.pays === "horsCoteDivoire" && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Précisez le pays" name="autrePays" value={data.autrePays || ""} onChange={handleChange} />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                select fullWidth label="Ville" name="ville"
                value={data.ville || ""} onChange={handleChange}
                InputProps={{ startAdornment: <InputAdornment position="start"><LocationCityIcon /></InputAdornment> }}
              >
                {options.villes.map((opt) => (<MenuItem key={opt.key} value={opt.key}>{opt.name}</MenuItem>))}
              </TextField>
            </Grid>
            {data.ville === "autre" && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Précisez la ville" name="autreVille" value={data.autreVille || ""} onChange={handleChange} />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              {data.ville === "abidjan" ? (
                <TextField
                  select fullWidth label="Commune" name="commune"
                  value={data.commune || ""} onChange={handleChange}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocationCityIcon /></InputAdornment> }}
                >
                  {abidjanCommunesList.map((opt) => (<MenuItem key={opt.code} value={opt.code}>{opt.libelle}</MenuItem>))}
                </TextField>
              ) : (
                <TextField fullWidth label="Commune" name="commune" value={data.commune || ""} onChange={handleChange} />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Quartier" name="quartier" value={data.quartier || ""} onChange={handleChange} />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DialogParticipantContent;